import { DeepPartial, ID } from "@/core/types";
import { BasketItem, BasketItemProps, BasketRule, Modificator, Product } from "@/gql/gen";
import { ProductModel } from "@/modules/shop/composable/useProduct";
import { BasketItemModel } from "@/modules/shop/composable/util";

export const basketItemPriceRound = (price: number) => {
    return Math.round(price)
}

/**
 * Заполнить элемент корзины данными товара
 * 
 * @param item - Частичные данные элемента корзины
 * @param product - Модель товара
 * @param reasonModificator - Модификатор (для товаров-конструкторов с PARENT_UUID)
 * @param reasonRule - Правило корзины (для товаров special без PARENT_UUID)
 */
export function fillBasketItemByProduct(
    item: DeepPartial<BasketItem>, 
    product: ProductModel, 
    reasonModificator?: Modificator,
    reasonRule?: BasketRule
) {
    let props = {
        INGREDIENTS_ADDED: [],
        INGREDIENTS_REMOVED: [],
        ...(item.PROPERTIES || {})
    } as unknown as BasketItemProps
    
    // REASON_MOD_ID - только для товаров-конструкторов (с PARENT_UUID)
    if (reasonModificator) {
        props = {
            ...props,
            REASON_MOD_ID: reasonModificator.ID,
            REASON_MOD_CODE: reasonModificator.GROUP_CODE,
        }
    }
    
    // REASON_RULE_ID - для товаров по правилам корзины (special, gift)
    // Товары БЕЗ PARENT_UUID (корневые элементы)
    if (reasonRule) {
        props = {
            ...props,
            REASON_RULE_ID: reasonRule.ID,
            REASON_RULE_CODE: reasonRule.CODE || null,
        }
    }
    
    Object.assign(item, {
        QUANTITY: item.QUANTITY || 1,
        PRODUCT_ID: item.PRODUCT_ID || product.ID,
        PRICE_BASE: product.getPriceSource(),
        PRICE: product.getPriceSource(),
        NAME: product.NAME,
        ELEMENT: product,
        PROPERTIES: props,
        MODIFICATORS: {}
    })
    return item as BasketItem
}

export type BasketItemDescriptor = ID | BasketItem | BasketItemModel

export type TBasketOpRemoveExcess = {
    action: 'remove-excess',
}

export type TBasketOpAdd = {
    action: 'add',
    quantity: number
    productId?: number
    itemId?: number
    itemUuid?: string
    item?: BasketItemModel
    product?: ProductModel | Product
}

export type TBasketOpSetQuantity = {
    action: 'quantity',
    itemId?: number
    itemUuid?: string
    item?: BasketItemModel
    productId?: number
    quantity: number
}

export type TBasketOpDelete = {
    action: 'delete',
    itemId?: number
    itemUuid?: string
    item?: BasketItemModel
    productId?: number
}

export type TBasketOpClear = {
    action: 'clear',
}

export type TBasketOpAddMultipleProduct = {
    itemId?: number
    productId?: number
    product?: ProductModel
    quantity: number
}

export type TBasketOpAddMultiple = {
    action: 'add-multiple',
    products: TBasketOpAddMultipleProduct[],
}

export type TBasketOpAddConstructor = {
    action: 'add-constructor',
    constructor: any
    sectionId: number
    sectionCode: string
}

export type TBasketOp = TBasketOpAdd
    | TBasketOpAddMultiple
    | TBasketOpAddConstructor
    | TBasketOpSetQuantity
    | TBasketOpDelete
    | TBasketOpClear
    | TBasketOpRemoveExcess

export type BasketModificatorInfo = {
    mod: Modificator,
    quantity: number,
    productIds: Record<string, number>
}

export type BasketModificatorsByProductLimit = {
    mod: Modificator,
    quantity: number,
    count: number,
    countExcess: boolean
}

export type BasketModificatorsByProductLimits = Record<string, BasketModificatorsByProductLimit>

export type BasketItemStatus = {
    mod?: Modificator
    quantity: number
    quantityLimit: number
    quantityAvailable: number
    haveError?: boolean
}

export type BasketItemError = {
    type: 'schedule'
    message: string
    icon?: string | boolean
    link?: string
}

export type BasketCalculationMode = 'server' | 'client' | 'client-server'



